if (!customElements.get('sticky-add-to-cart')) {
  /* What the bar does in each <buy-box> state. The bar has two behaviours of its
     own — 'notify', its brown sign-up, and 'mirror', copying the main button's
     disabled state and label — and this says which, per state, rather than
     letting the mirror be an implicit default. A state added to <buy-box>
     without a row here reaches the fallback below, which is loud in review
     precisely because the mirror is the wrong answer for anything new: it would
     paint the main button's greyed "Sold out" over whatever the new state
     actually offers.

     'link' is the third: the size is only buyable on the other generation, so
     the bar swaps its submit for the anchor rendered beside it and carries the
     same destination the buy box does. Mirroring there would paint a greyed
     "Sold out" over the one thing the shopper can still do.

     'launch' is the fourth, and the one state with no main button to mirror at
     all: before launch the buy row is not rendered, so the bar carries the
     launch sign-up's own CTA and a tap takes the shopper to that field.

     `instore` takes the same path: a retail exclusive has no buy row either,
     and the bar's tap goes to the panel's booking block, which carries the
     same data-buy-box-launch hook. */
  const STICKY_BAR_BEHAVIOUR = {
    select: 'mirror',
    available: 'mirror',
    preorder: 'mirror',
    soldout: 'notify',
    restricted: 'mirror',
    alternate: 'link',
    comingsoon: 'launch',
    instore: 'launch',
  };

  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.sectionId;
        this.mainButton = document.getElementById(`ProductSubmit-${sectionId}`);
        this.mainPrice = document.getElementById(`price-${sectionId}`);
        this.buyBox = document.querySelector(`buy-box[data-section="${sectionId}"]`);
        this.notifyTrigger = this.buyBox?.querySelector('[data-buy-box-notify-trigger]');
        this.captureSlot = this.buyBox?.querySelector('[data-buy-box-capture]');
        // The buy box already resolves the destination for the selected size and
        // writes it synchronously on every size change, so the bar reads that
        // link rather than keeping a second copy of the size-to-size map.
        this.alternateLink = this.buyBox?.querySelector('[data-buy-box-alternate]');
        this.stickyButton = this.querySelector('.sticky-atc__button');
        this.stickyLink = this.querySelector('.sticky-atc__link');
        // The inner text span, not the label: the label also holds the notify
        // bell, and every state change writes textContent on this element.
        // Scoped to the button, because the link carries a text span too.
        this.stickyLabel = this.stickyButton?.querySelector('.sticky-atc__button-text');
        this.stickySpinner = this.stickyButton?.querySelector('.loading-overlay__spinner');
        this.stickyPrice = this.querySelector('.sticky-atc__price');
        this.launchButton = this.querySelector('[data-sticky-launch]');
        this.guardEvents = [];

        // Before launch there is no main button and no size control — the whole
        // buy row is unrendered — so the bar takes its own path: reveal on the
        // same anchor, and one tap that leads to the sign-up field. No price
        // sync either: with no size control the quoted price cannot change.
        //
        // Read straight off data-state rather than through behaviour(), which
        // waits for <buy-box> to upgrade: nothing on this path needs it to. The
        // launch form is server-rendered and visible whether or not buy-box.js
        // arrives, this file owns the tap, and coming-soon is the one state no
        // size pick can leave — so the server's answer is the whole answer.
        if (this.launchButton && STICKY_BAR_BEHAVIOUR[this.buyBox?.dataset.state] === 'launch') {
          this.stripPriceIds();
          this.initVisibility();
          this.initLaunchClick();
          this.initViewportMeasure();
          return;
        }

        // Without the main buy box there is nothing to mirror or anchor to.
        if (!this.mainButton) return;

        // The bar re-renders the price snippet, whose markup carries ids that already
        // exist in the main price block; drop them so the DOM has no duplicate ids.
        this.stripPriceIds();

        this.initVisibility();
        this.initSheetDismiss();
        this.initPriceSync();
        this.initSizeGuard();
        this.initClick();
        this.initButtonSync();
        this.initViewportMeasure();
      }

      // The bar's height changes outside reveal: iOS collapsing its toolbar
      // flips env(safe-area-inset-bottom) by ~34px, and a height measured in
      // the other toolbar state leaves the body offset short — the bar covers
      // the last content at full scroll and the lifted chat launcher sits low.
      // visualViewport fires for toolbar collapse where window resize may not;
      // rAF-collapsed because it fires continuously through the animation.
      initViewportMeasure() {
        this.onViewportResize = () => this.scheduleMeasure();
        this.viewportTarget = window.visualViewport || window;
        this.viewportTarget.addEventListener('resize', this.onViewportResize);
      }

      // One pending frame for every burst source (viewport resize, price
      // re-render); the frame reads the DOM as it stands when it runs.
      scheduleMeasure() {
        if (this.hidden || this.measureRaf) return;
        this.measureRaf = requestAnimationFrame(() => {
          this.measureRaf = null;
          this.measureHeight();
        });
      }

      // An open bottom sheet leaves the bar tappable above it, but everything a tap
      // does — add, scroll to the size field, open the sign-up — happens on the
      // page behind the sheet, so the sheet closes first. Capture phase, so it runs
      // before the button's own handler scrolls anywhere.
      initSheetDismiss() {
        this.onSheetDismiss = () => document.querySelector('.product-popup-modal--sheet[open]')?.hide();
        this.addEventListener('click', this.onSheetDismiss, true);

        // The sheet's bottom edge is placed from --sticky-atc-height, but the
        // bar only re-measures while shown, and a sheet can open while it is
        // hidden after a toolbar change; offsetHeight is valid while hidden.
        this.sheetObserver = new MutationObserver((records) => {
          if (records.some((record) => record.target.hasAttribute('open'))) this.measureHeight();
        });
        document.querySelectorAll('.product-popup-modal--sheet').forEach((sheet) => {
          this.sheetObserver.observe(sheet, { attributes: true, attributeFilter: ['open'] });
        });
      }

      disconnectedCallback() {
        if (this.onSheetDismiss) this.removeEventListener('click', this.onSheetDismiss, true);
        this.sheetObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.buyBoxObserver?.disconnect();
        if (this.onStickyClick) this.stickyButton?.removeEventListener('click', this.onStickyClick);
        if (this.onLaunchClick) this.launchButton?.removeEventListener('click', this.onLaunchClick);
        if (this.onSizeEngaged && this.sizeField) {
          this.guardEvents.forEach((evt) => this.sizeField.removeEventListener(evt, this.onSizeEngaged));
        }
        clearTimeout(this.highlightTimer);
        if (this.onViewportResize) {
          this.viewportTarget.removeEventListener('resize', this.onViewportResize);
        }
        cancelAnimationFrame(this.measureRaf);
        document.body.classList.remove('sticky-atc-visible');
      }

      // A single switch for the bar: drives the slide-in and the body class that
      // offsets page content and the chat launcher while the bar is on screen.
      setVisible(visible) {
        if (visible) this.measureHeight();
        this.hidden = !visible;
        document.body.classList.toggle('sticky-atc-visible', visible);
      }

      // Publish the bar's real height (incl. safe-area padding) so the page offset
      // and the lifted chat launcher track it exactly instead of a guessed value.
      measureHeight() {
        const height = this.offsetHeight;
        if (height > 0) document.body.style.setProperty('--sticky-atc-height', `${height}px`);
      }

      stripPriceIds() {
        if (!this.stickyPrice) return;
        this.stickyPrice.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      }

      // Reveal the bar only once the main call to action is out of view. Anchored on
      // <buy-box>, which stays laid out in every state: the sold-out state hides
      // .product-form__buttons, and a display:none anchor never intersects anything,
      // so anchoring on the submit button would show the bar from the top of the page.
      initVisibility() {
        if (!('IntersectionObserver' in window)) {
          this.setVisible(true);
          return;
        }
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            this.setVisible(!entries[0].isIntersecting);
          },
          { rootMargin: '0px 0px -10% 0px' }
        );
        this.visibilityObserver.observe(this.buyBox || this.mainButton);
      }

      // The main price container is re-rendered on variant change; mirror it.
      initPriceSync() {
        if (!this.mainPrice || !this.stickyPrice) return;
        this.priceObserver = new MutationObserver(() => {
          this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
          this.stripPriceIds();
          // The mirrored block can gain a badge (e.g. "Sold out")
          // after the section fetch lands, taller than what was there when the
          // bar last published its height — re-measure here too, not just on
          // the button/link swap, or the bar (and the body offset it drives)
          // trails one pick behind. Coalesced, since childList+subtree can fire
          // more than once per render.
          this.scheduleMeasure();
        });
        this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true });
      }

      // A tap must never add a size the buy box would not add. The control itself
      // answers whether one is chosen, in both of the PDP's starting positions:
      //
      //   gate suppressed  no variant is preselected, the select opens on a blank
      //                    disabled <option>, so its value is empty until they pick
      //   otherwise        a variant is preselected — by a ?variant= deep link, or by
      //                    Shopify where size-preselect-gate.liquid leaves the page
      //                    alone (a single-size run, a price-varies product such as
      //                    the dust bag). The buy box already sells that variant in
      //                    one tap, so the bar mirrors it from the first paint
      //
      // The two differ in what happens next: a suppressed select can be cleared, so it
      // re-reads; a preselected one only ever gains engagement, so it latches.
      //
      // Guards only a real SIZE choice (the fit/returns risk) — not colour/other
      // options, where the copy would be wrong and the pre-selected value is fine.
      // If no such control is found the guard stays off and the bar adds as before
      // (fail open — never blocks a legitimate add).
      initSizeGuard() {
        const sectionId = this.dataset.sectionId;
        const picker =
          document.querySelector(
            `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`
          ) ||
          this.mainButton
            .closest('product-info, .product, section')
            ?.querySelector('variant-selects, variant-radios');

        // Match the theme's own size-option names. EN/DE/FR are all live, and
        // option names are translatable even though they aren't translated yet.
        const sizeNames = ['size', 'taille', 'größe'];
        const isSize = (name) => sizeNames.includes((name || '').trim().toLowerCase());

        let count = 0;
        let gateSuppressed = false;
        if (picker) {
          const select = Array.from(picker.querySelectorAll('select')).find((s) => {
            const match = s.name.match(/^options\[(.+)\]$/i);
            return match && isSize(match[1]);
          });
          if (select) {
            this.sizeControl = select;
            // The blank disabled <option value=""> is what main-product.liquid renders
            // when the preselect gate fires, and it is what makes "no size chosen" real
            // for the buy path too — updateOptions() matches no variant on ''. Its
            // presence is therefore the honest test for which mode this PDP is in, and
            // unlike buy-box's data-state it does not move as the shopper browses.
            // It also adds one to the option count, which the count > 1 test below
            // survives: size-preselect-gate.liquid only suppresses an option with more
            // than one value, so a suppressed select carries three options at minimum.
            gateSuppressed = Boolean(select.querySelector('option[value=""]'));
            count = select.options.length;
          } else {
            const fieldset = Array.from(picker.querySelectorAll('fieldset')).find((fs) =>
              isSize(fs.querySelector('legend')?.textContent)
            );
            if (fieldset) {
              this.sizeControl = fieldset.querySelector('input[type="radio"]');
              count = fieldset.querySelectorAll('input[type="radio"]').length;
            }
          }
        }

        this.guardActive = Boolean(this.sizeControl) && count > 1;
        this.sizeChosen = !this.guardActive;
        if (!this.guardActive) return;

        this.sizeField = this.sizeControl.closest('.product-form__input') || this.sizeControl;

        // Once <size-picker> upgrades, the select is visually hidden and the chips
        // are what the shopper can actually see. Resolved at prompt time, not here,
        // because the checked chip changes as they browse. Falls back to the select.
        // Gated on the definition, not on the markup: with component-size-picker.js
        // blocked the chips are still in the DOM but the grid is display:none
        // (the :not(:defined) rule in its CSS), and focusing inside it no-ops.
        this.getFocusTarget = () =>
          (customElements.get('size-picker') &&
            (this.sizeField.querySelector('.size-picker__chip input:checked') ||
              this.sizeField.querySelector('.size-picker__chip input'))) ||
          this.sizeControl;

        // Both picker shapes carry the answer, and the suppressed form of each is what
        // reads as unchosen: the select sits on the blank option, the radio group ships
        // with nothing checked (main-product.liquid drops `checked` under the same gate).
        const readChosen = () =>
          this.sizeControl.type === 'radio'
            ? Boolean(this.sizeField.querySelector('input[type="radio"]:checked'))
            : Boolean(this.sizeControl.value);

        if (gateSuppressed) {
          // The blank option is disabled, so the shopper cannot return to it, but a
          // script writing the select (Ecomid's size finder does) could leave it
          // empty; this reads rather than latches. A chip pick reaches here because
          // component-size-picker.js writes the select and dispatches a bubbling
          // change on it.
          this.sizeChosen = readChosen();
          this.onSizeEngaged = () => {
            const chosen = readChosen();
            if (chosen === this.sizeChosen) return;
            this.sizeChosen = chosen;
            this.syncButton?.();
          };
          this.guardEvents = ['change'];
        } else {
          // The preselected size is what the buy box sells, so the bar starts unblocked
          // and agrees with it. Engagement still latches, for the radio picker:
          // its suppressed form has no blank option to detect and lands here with
          // nothing checked. Opening (pointerdown/keydown) counts as engaging, because
          // someone who wants the pre-selected size can't fire a change by re-picking it.
          this.sizeChosen = readChosen();
          this.onSizeEngaged = () => {
            if (this.sizeChosen) return;
            this.sizeChosen = true;
            this.syncButton?.();
          };
          this.guardEvents = ['change', 'pointerdown', 'keydown'];
        }

        this.guardEvents.forEach((evt) => this.sizeField.addEventListener(evt, this.onSizeEngaged));
      }

      // The bar is type="submit" form="…", so every state in which it must not add to
      // the cart needs its click intercepted here. Attached unconditionally: notify
      // mode exists whether or not the size guard is active.
      initClick() {
        if (!this.stickyButton) return;
        this.onStickyClick = (event) => {
          if (this.isNotifyState()) {
            event.preventDefault();
            this.promptForNotify();
            return;
          }
          if (this.guardActive && !this.sizeChosen) {
            event.preventDefault();
            this.promptForSize();
          }
        };
        this.stickyButton.addEventListener('click', this.onStickyClick);
      }

      // The launch CTA never submits anything — it is a plain button outside any
      // form — so this only has to carry the shopper to the field it names.
      initLaunchClick() {
        this.onLaunchClick = () => this.promptForLaunch();
        this.launchButton.addEventListener('click', this.onLaunchClick);
      }

      // The sign-up block, not the input: scrolling to the field alone would put
      // the eyebrow and the launch date off screen above it, and those are what
      // say what the shopper is signing up for.
      promptForLaunch() {
        const launch = this.buyBox?.querySelector('[data-buy-box-launch]');
        if (!launch) return;
        this.scrollIntoCentre(launch);
        // The booking block of a retail exclusive has no field; its first action
        // ("Email the store") takes focus instead, so a keyboard or VoiceOver
        // user lands where the tap sent them rather than back on the bar.
        const target = launch.querySelector('input[type="email"]') || launch.querySelector('a[href]');
        target?.focus({ preventScroll: true });
      }

      // <buy-box> owns the state; the bar only reads it, so the two can never
      // disagree about whether the selected variant is buyable.
      //
      // The method, not the element: data-state is server-rendered, so a blocked
      // buy-box.js leaves a truthful 'soldout' on an element that will never
      // unhide its notify trigger — the bar would paint a brown button that opens
      // nothing. Failing the test drops the bar back to mirroring the main button,
      // which the server already rendered disabled and labelled "Sold out".
      behaviour() {
        if (typeof this.buyBox?.showCapture !== 'function') return 'mirror';
        return STICKY_BAR_BEHAVIOUR[this.buyBox.dataset.state] || 'mirror';
      }

      isNotifyState() {
        return this.behaviour() === 'notify';
      }

      scrollIntoCentre(target) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }

      promptForSize() {
        this.scrollIntoCentre(this.sizeField);
        (this.getFocusTarget ? this.getFocusTarget() : this.sizeControl).focus({ preventScroll: true });

        this.sizeField.classList.add('sticky-atc-size-highlight');
        clearTimeout(this.highlightTimer);
        this.highlightTimer = setTimeout(
          () => this.sizeField.classList.remove('sticky-atc-size-highlight'),
          2000
        );
      }

      // The trigger's own handler is what opens the field, so this clicks it rather
      // than reaching into <buy-box>'s internals — buy-box.js's showCapture() now
      // owns getting the field into view itself (preventScroll + a rAF-delayed
      // scrollIntoView), so this no longer needs a pre-scroll of its own.
      //
      // If the field is already open, the trigger is hidden and a click would do
      // nothing — this falls back to bringing the existing field back into view,
      // for a shopper who scrolled away from an already-open capture and tapped
      // the bar's "Notify me" again.
      promptForNotify() {
        if (this.notifyTrigger && !this.notifyTrigger.hidden) {
          this.notifyTrigger.click();
          return;
        }
        if (this.captureSlot && !this.captureSlot.hidden) {
          this.scrollIntoCentre(this.captureSlot);
          this.captureSlot.querySelector('input[type="email"]')?.focus({ preventScroll: true });
        }
      }

      // Keep availability, label and loading state in step with the main button
      // (sold out, in-flight) so the two buttons never disagree. Two states are the
      // bar's own: notify, mirroring <buy-box>'s brown sign-up, and the pre-choice
      // "Select your size" CTA (kept enabled even when the pre-selected variant is
      // sold out, since another size may be in stock).
      initButtonSync() {
        if (!this.stickyButton) return;
        const sync = () => {
          // Ahead of everything, including the size guard: the link state names a
          // size by definition, and the bar's own copy is the wrong element for it.
          // Both halves are written on every pass so returning to a buyable size
          // brings the button back — the link is not a one-way door.
          if (this.stickyLink) {
            // The buy box's own link is what carries the destination; without it
            // there is nothing to copy, so the bar keeps its button rather than
            // swapping in a link that points nowhere. The label stays the
            // server-rendered size-less one; the in-page panel says where the size is.
            const linksOut = this.behaviour() === 'link' && !!this.alternateLink;
            if (linksOut) this.stickyLink.href = this.alternateLink.href;
            const swappedHalf = linksOut !== this.linksOut;
            this.linksOut = linksOut;
            this.stickyLink.hidden = !linksOut;
            this.stickyButton.hidden = linksOut;
            // The link and button halves render at different heights (roughly
            // 106px vs 69px at 390px wide), so whichever one becomes visible has to
            // re-publish --sticky-atc-height, in either direction — a pick back
            // to a buyable size that skipped this left the bar (and the body
            // padding and Gorgias launcher offset it drives) pinned at the
            // link's taller value.
            if (swappedHalf && !this.hidden) this.measureHeight();
            if (linksOut) {
              return;
            }
          }

          // Ahead of the size guard: reaching soldout at all means a size was named,
          // by a pick or by the ?variant= deep link that suppressed the gate. Behind
          // the guard the bar would tell the shopper to choose a size they just chose,
          // and would disagree with the buy box they are being sent to.
          if (this.isNotifyState()) {
            this.stickyButton.classList.add('sticky-atc__button--notify', 'button--notify');
            this.stickyButton.classList.remove('sticky-atc__button--needs-size', 'loading');
            this.stickyButton.disabled = false;
            this.stickySpinner?.classList.add('hidden');
            const notify = this.notifyTrigger?.textContent.trim() || window.variantStrings?.notify;
            if (this.stickyLabel && notify) this.stickyLabel.textContent = notify;
            return;
          }
          this.stickyButton.classList.remove('sticky-atc__button--notify', 'button--notify');

          if (this.guardActive && !this.sizeChosen) {
            this.stickyButton.disabled = false;
            this.stickyButton.classList.add('sticky-atc__button--needs-size');
            this.stickyButton.classList.remove('loading');
            this.stickySpinner?.classList.add('hidden');
            if (this.stickyLabel && this.dataset.selectSizeText) {
              this.stickyLabel.textContent = this.dataset.selectSizeText;
            }
            return;
          }
          this.stickyButton.classList.remove('sticky-atc__button--needs-size');

          const disabled =
            this.mainButton.hasAttribute('disabled') ||
            this.mainButton.getAttribute('aria-disabled') === 'true';
          this.stickyButton.disabled = disabled;

          const label = this.mainButton.querySelector('span');
          if (label && this.stickyLabel) this.stickyLabel.textContent = label.textContent.trim();

          const loading = this.mainButton.classList.contains('loading');
          this.stickyButton.classList.toggle('loading', loading);
          this.stickySpinner?.classList.toggle('hidden', !loading);
        };
        this.syncButton = sync;
        sync();
        this.buttonObserver = new MutationObserver(sync);
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });

        // The sold-out state hides the whole button row rather than relabelling it, so
        // nothing on the main button changes when it is entered — the state attribute
        // is the only signal.
        if (this.buyBox) {
          this.buyBoxObserver = new MutationObserver(sync);
          this.buyBoxObserver.observe(this.buyBox, {
            attributes: true,
            attributeFilter: ['data-state'],
          });
        }
      }
    }
  );
}
